/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

export {};

declare var analytics: any;

const merge = require("lodash.merge");

let hostname = "localhost";

if (typeof window !== "undefined") {
	hostname = window.location.hostname;
}

// Check if running locally
const DEBUG = hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("ngrok.io");

const Config: any = {
	analyticsTrackingID: false,

	server: "https://api.vi.vatom.network",
	websocketServer: "wss://ws.vatominc.net",
	oauthServer: process.env.IDENTITY_API_URL,
	isVatomicOIDC: true,
	oidcAuthority: process.env.IDENTITY_API_URL,
	studioServer: "https://studio.vatom.com",

	// billingUrl: 'http://localhost:3002',

	// Local storage prefix to separate multiple instances hosted on the same domain
	prefix: "viewer",

	// Register native faces
	// nativeFaces: [
	//     require('@blockv/3d-face'),
	//     require('./Faces/VideoFace'),
	//     require('./Faces/SpriteFace')
	// ],

	// Reply address for the feedback email
	feedbackEmail: "Vatom Support <support@vatom.com>",
	feedbackURL: "https://feedback.vatom.com",

	// URL for the Terms of Use
	termsOfUseURL: "https://resources.vatom.com/system/terms/04212022/terms.pdf",

	// URL to the Privacy Policy
	privacyPolicyURL: "https://resources.vatom.com/system/privacy/04212022/privacy-policy.pdf",

	// URL to the Cookie Policy
	cookiePolicyURL: "https://resources.vatom.com/system/privacy/04042020/privacy-policy.pdf",

	// Application Title
	appTitle: DEBUG ? "Vatom Wallet [Dev]" : "Vatom Wallet",

	// Consent version of the app
	consentDocumentVersion: 1,

	geoLocationApi:
		"https://api.ipstack.com/check?access_key=f6c1392b737ae773c32e839ccd03e87f&fields=country_code,latitude,longitude",

	// Show warning icon on a vatom if it's configuration is not correct
	vatomWarningIcons: DEBUG ? true : false,

	// Custom error message replacer. This allows you to replace the text that appears for certain errors.
	// Example:
	//
	//      { code: 1015, text: "You need to verify your account first.", title: "Verify required" }
	//
	customErrors: [],

	// Options relating to the external config file. Note, these options can't be changed from within the remote config.
	remoteConfig: {
		// Remote config URL. Leave blank to disable the remote config. Any keys specified will override
		// keys from this file. You can also use these placeholders inside the URL:
		//
		// - $HOSTNAME : eg. "vatom.com"
		//
		// Examples:
		// - "https://s3.amazon.com/mybucket/$HOSTNAME.json"
		//
		// url: "https://businesses.api.vatominc.com/viewers/config?hostname=" + hostname,
		url: "https://businesses.api.vatominc.com/viewers/config?hostname=developer.vatominc.coo",

		// Amount of time between checks for a new config version. Set to 0 to disable.
		checkInterval: 1000 * 60 * 5,

		// How to update the remote config once a new version is found. Options are:
		// - 'notify' : A banner in the inventory is shown to the user, asking them to install the update.
		// - 'reload' : The app is immediately reloaded. Note, the user could lose unsaved work.
		// - 'force' : The update is applied while the app is running. Note, this may result in unexpected behaviour, use with caution.
		updateMode: "notify",
	},

	// Filter all vatoms by a specified field and value. Examples:
	// - { field: "vAtom::vAtomType.template", value: "..." } will only show vatoms with a specific template
	// - { field: "vAtom::vAtomType.author", value: "..." } will only show vatoms created by a specific user
	// - { field: "private.campaign_id", value: "..." } will only show vatoms in a specific campaign (custom field)
	filter: {
		field: "",
		value: "",
	},

	// Enabled features
	features: {
		accounts: {
			autoCreateGuest: false,
			disableRegistration: true,
			hasStarterPack: true,
		},
		campaignHeader: false,
		showBookmarkAlert: true,
		login: {
			mode: "oauth",
			supportEmail: true,
			supportPhone: true,
			supportGuest: false,
			// Can be 'explicit', 'implicit', 'country' or 'none'. Country will be explicit unless US
			termsAndConditions: "implicit",
			showGuide: true,
		},

		registration: {
			// Can be 'optional', 'required'  or 'none' for not required
			password: "optional",
			verify: true,
			userName: "none",
		},

		// App header options
		header: {
			connect: true,
			home: true,
			map: true,
			scanner: true,
			ar: true,
			activity: true,
			profile: "desktop",
			logo: true,
			subLogo: false,
			category: false,
			search: true,
			settings: true,
		},
		businessHeader: {
			title: true,
		},
		incomingToast: {
			enabled: true,
		},
		// Inventory options
		inventory: {
			// Show the warning triangles and errors when loading the view
			showViewLoadErrors: false,

			// Comma separated list of fqdn's to whitelist. The filtering is done client-side.
			fqdn_whitelist: [],

			// Show a "Synchronizing..." alert while the inventory is loading.
			synchronizing_alert: true,

			// Force scanner to redirect to inventory when clicking on close button
			close_scan_to_inventory: true,

			// Free Token Url
			freeTokenUrl:
				"https://share.vatom.com/emit/eyJvYmplY3REZWZpbml0aW9uSWQiOiJrQjlVOXlNMm1wIiwiY2FtcGFpZ25JZCI6Iko1MjVQaEZMeDUifQ%3D%3D?from=Vatom%2520Inc.&msg=&login=1",
		},
		// This is a placeholder for the footer nav.
		footer: {
			home: true,
			connect: true,
			profile: true,
		},

		// Map options
		maps: {
			// A single FQDN to whitelist. This performs filtering server-side.
			fqdn_whitelist: "",

			// Maximum vatoms to load in AR by default. Using a high number could cause crashes.
			max_items: 20,
			pickup: {
				enabled: true,
				redirect: "ar",
			},
		},
		// AR options
		ar: {
			maxItems: 5,

			// Radius around the user to fetch vatoms, in meters
			visibleRadius: 500,

			// Should we move objects closer to / farther from the camera, and what distance multiplier should we use? 1 = off
			distanceMultiplier: 0.05,

			// Minimum distance from the camera, in meters. If an object is closer than this, it will be moved this far away.
			minDistance: 1,

			// A single FQDN to whitelist. This performs filtering server-side.
			fqdnWhitelist: "",

			// Attempt to synchronise the camera feed to the 3D renderer. Requires more processing power.
			cameraSync: false,

			// Display debug info in the AR feed.
			debugInfo: DEBUG ? true : false,

			// Display a debug floor grid
			floorGrid: false,

			showRecord: true,
		},

		/**
		 * @enable_country_restriction set to false will ignore all country specific actions
		 */
		localization: {
			enable_country_restriction: false,
			eligible_countries: ["US"],
			eligible_states: ["California"],
			eligible_state_text: "",
			eligible_state_subtext: "",
		},

		legalities: {
			agegate: false,
			agegate_text: "",
			agegate_subtext: "",
		},

		vatom: {
			menu: true,
			sendShare: true,
			supportEngaged: true,
			closeToInventory: false,
		},
		faces: {
			video: {
				allowSkip: true, // If the vatom is a crate, choose whether skip isd allowed

				// These settings are only applicable when the vatom is NOT a crate
				muted: true, // always 'true' for crates
				autoplay: true, // always 'true' for crates
				controls: true, // always 'false' for crates
			},
		},
		fullScreenScroll: true,
	},
	// This should go in an i18n framework
	text: {
		login: {
			title: "Welcome to the Vatom Viewer",
			subTitle: "Log in or Register to access your digital stuff",
			guestLoginButton: "Let's Go!",
			submit: "Submit",
			continue: "Continue",
			skip: "Skip",
			codeSent: "Please enter the code we sent to",
		},
		loginPopup: {
			title: "Add your email to secure your account",
			subTitle: "This will ensure that you can always get back to your individual account",
		},
		termsAndConditions: {
			explicitHtml:
				"By clicking this, you agree to our <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/terms.pdf'>Terms of Use</a>, <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Privacy Policy</a> and <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Cookies Policy</a>. You may receive SMS Notifications from us and can opt out any time.",
			implicitHtml:
				"By clicking Login or Register, you agree to our <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/terms.pdf'>Terms of Use</a>, <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Privacy Policy</a> and <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Cookies Policy</a>. You may receive SMS Notifications from us and can opt out any time.",
			dialogContentHtml:
				"By using this app, you agree to our <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/terms.pdf'>Terms of Use</a>, <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Privacy Policy</a> and <a target='_blank' href='https://www.vatominc.com/wp-content/uploads/2020/06/privacy-policy.pdf'>Cookies Policy</a>. You may receive SMS Notifications from us and can opt out any time.",

			/**
			 * Array of custom html to be displayed on the consent dialog, below the dialogContentHtml above
			 * @param text is a html string
			 * This will be parsed on the consent popup
			 *
			 * eg
			 * {
			 *   text: '<p>Some text here: <a href="http://www.google.com">Google</a></p>'
			 * }
			 */
			dialogCustomHtml: "",
		},

		map: {
			pickup: {
				// buttonText: "Open AR"
			},
		},
	},
	theme: {
		button: {
			backgroundColor: "#1872E0",
			borderColor: "#fff",
			color: "#fff",
		},
		header: {
			color: "#212529",
			// borderColor: "#e1e1e1",
			// borderStyle: "solid",
			// borderWidth: "0 0 1px 0",
			logo: require("./res/logo.png").default,
		},
		headerNav: {
			borderBottom: "4px solid rgba(0, 0, 0, 0)",
		},
		headerNavSelected: {
			borderBottom: "4px solid #1872E0",
		},
		inventory: {
			// backgroundColor: "#F8F9FA",
		},
		login: {
			termsAndConditions: {
				borderColor: "#000",
				color: "#000",
			},
			button: {
				borderTopRightRadius: 0,
				borderBottomRightRadius: 0,
				borderTopLeftRadius: 0,
				borderBottomLeftRadius: 0,
			},
			base: {},
			frame: {},
			guide: {},
			card: {},
		},
		popup: {
			border: "solid 1px #1872e0",
			backgroundColor: "#fff",
			// backgroundImage: 'linear-gradient(to bottom, #ffffffd9 53%, #deedff 76%, #7db0ef)',
			borderRadius: 8,
			// backdropFilter: 'blur(4px)',
			// WebkitBackdropFilter: 'blur(3px)',
			// boxShadow: '0 -1px 2px 1px #fff8cc90, 0 -3px 11px 5px #8098a43d'
		},

		mui: {},
	},

	// Custom external links (displayed in profile)
	/**
	 * @title text to be displayed for the link
	 * @url url of the link
	 *
	 * eg. { title: 'google', url: 'https://www.google.com'}
	 */
	customLinks: [],

	/**
	 *  This is a custom analytics option
	 *  @method onInit()  sets up the tracking js file
	 *
	 *  @method onEvent() catches all events and sends them to
	 *  the desired platform for tracking
	 *
	 *  TODO: add google analytics tracking info
	 */

	analytics: {
		onInit: () => {},
		onEvent: (name: string, args: any) => {
			if (typeof analytics !== "undefined") {
				// console.info("TRACK: ", analytics, name)
				analytics.track(name, args);
			}
		},
		onIdentify: (userId: string, args: any) => {
			// console.info("In onIdentify: ", analytics)
			if (typeof analytics !== "undefined") analytics.identify(userId, args);
		},
	},
};

//
// Include secret config options (like AppID, firebase token etc)
merge(Config, require("./config-main.js"));

// If we're hosted on the DEV server, use the DEV environment instead
if (hostname.includes("blockv.net")) {
	merge(Config, {
		// Your developer key. You can get one from developer.blockv.io
		appID: "9d9bb2f3-0193-475c-b4bd-c9b30ae4a90b",

		// The server to use
		server: "https://apidev.blockv.net",
		websocketServer: "wss://ws.blockv.net",
		oauthServer: "https://login.blockv.net",

		// Local storage prefix to separate multiple instances hosted on the same domain
		prefix: "viewer_dev",

		// Application Title
		appTitle: "Vatoms [Dev]",

		// Show warning icon on a vatom if it's configuration is not correct
		vatomWarningIcons: true,
	});
}

export default Config;
